import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { MultiGameLauncherPlayingType } from "@obg/gaming.external/public-api";
import { CoreContentState, TranslationSelector } from "@obg/ngx.core.content";
import { RouteLink, RouteLinkService, RouteService } from "@obg/ngx.router";
import { combineLatest, forkJoin, from, Observable, of } from "rxjs";
import { concatMap, last, map, switchMap } from "rxjs/operators";

import { GamingExternalState } from "../../../data/state";
import { GameThumbnailUrlParameters, GameThumbnailUrls, GameThumbnailUrlsProps } from "../game-thumbnail.model";

import { ARIA_LABELS_KEYS } from "./game-thumbnail-wrapper.const";

@Injectable({
	providedIn: "root",
})
export class GameThumbnailWrapperService {
	constructor(
		private translationSelector: TranslationSelector,
		private routeService: RouteService,
		private routeLinkService: RouteLinkService,
		private store: Store<GamingExternalState & CoreContentState>
	) {}

	getTranslatedAriaLabels$() {
		return of(ARIA_LABELS_KEYS).pipe(
			switchMap((labelsKeys) => {
				const observables: Observable<Record<string, string>>[] = [];
				for (const key in labelsKeys) {
					if (!labelsKeys.hasOwnProperty(key)) {
						continue;
					}

					observables.push(
						this.store.pipe(
							this.translationSelector.getByKey$(labelsKeys[key]),
							map((translatedString: string) => ({ [key]: translatedString }))
						)
					);
				}

				return combineLatest(observables).pipe(
					map((translatedStrings) => {
						let translatedAriaLabels = {};
						translatedStrings.forEach((translatedString) => {
							translatedAriaLabels = {
								...translatedAriaLabels,
								...translatedString,
							};
						});
						return translatedAriaLabels;
					})
				);
			})
		);
	}

	getUrls$(
		route: string,
		urls: GameThumbnailUrls,
		gameRoute: RouteLink | undefined,
		loginRoute: RouteLink,
		baseUrl: string | undefined,
		playForFunType: MultiGameLauncherPlayingType,
		replaceUrl: boolean
	) {
		this.routeService.loadModule(route.substring(0, route.indexOf(".")));
		const routeParamsKeys = Object.keys(GameThumbnailUrlsProps);

		const urls$ = routeParamsKeys.map((key: GameThumbnailUrlsProps) => {
			const queryParams = this.getQueryParams(key, loginRoute.route, baseUrl, playForFunType);
			if (!gameRoute) {
				return of(urls);
			}

			const routeToUse = queryParams?.useLoginRoute ? loginRoute : gameRoute;
			const extras = {
				replaceUrl: replaceUrl,
				...queryParams,
			};

			// this is done to prevent errors if "game or category" property is missing
			try {
				return this.routeLinkService.getUrl$(routeToUse, extras).pipe(
					map((url) => {
						urls[key] = url;
						return urls;
					})
				);
			} catch (error) {
				// continue regardless of error
				return of(urls);
			}
		});

		return forkJoin(urls$).pipe(
			concatMap((array) => from(array)),
			last()
		);
	}

	getQueryParams(
		key: GameThumbnailUrlsProps,
		loginRoute: string | undefined,
		baseUrl: string | undefined,
		playForFunType: MultiGameLauncherPlayingType
	): GameThumbnailUrlParameters | undefined {
		switch (key) {
			case GameThumbnailUrlsProps.baseUrl:
				return {
					queryParams: {},
				};
			case GameThumbnailUrlsProps.loginUrl:
				return {
					queryParams: {
						action: loginRoute,
					},
				};
			case GameThumbnailUrlsProps.practicePlayUrl:
				return {
					queryParams: {
						gameAction: playForFunType,
					},
				};
			case GameThumbnailUrlsProps.returnUrl:
				return {
					queryParams: {
						returnUrl: baseUrl,
					},
				};
			case GameThumbnailUrlsProps.returnLoginUrl:
				return {
					queryParams: {
						returnUrl: baseUrl,
					},
					useLoginRoute: true,
				};
			default:
				return undefined;
		}
	}
}
